/**
 * M3X.TRAVEL UX micro-sprint PR C — Full Per Diem catalog editor.
 *
 * Per BUILD_READINESS_M3X_TRAVEL_FULL.md §1.A.3 (PeopleHub screenshot 1):
 *
 *   - Left sidebar: categories list (CEO / GMs & Directors / Managers /
 *     Supervisors / custom) — click to switch
 *   - Top header: Business Travel ID + Description + Currency +
 *     Advance % + Return % + Pay with salary toggle
 *   - Tab toggle: Domestic / International
 *   - Body: per-destination per-day-range table with inline edit
 *   - Add / Remove row buttons
 *   - Save / Cancel with optimistic UI
 *   - Mutaba'aa floor enforcement inline (PR 2 enforces
 *     per_diem_amount > 0 at the API; we surface 400 detail messages
 *     inline so the editor explains the veto)
 */

import {
  Building2,
  CircleAlert,
  Crown,
  GripVertical,
  Plane,
  PlusCircle,
  Save,
  Trash2,
  UserCog,
  Users,
  X,
} from "lucide-react";
import { useEffect, useMemo, useState } from "react";
import { useTranslation } from "react-i18next";

import { PageHeader } from "../../components/ui";
import { useLegalEntityId } from "../../lib/legalEntity";


// ---- DTOs (mirror the API schemas in api/routes/travel_per_diem.py)

interface CategoryDTO {
  id: string;
  legal_entity_id: string;
  code: string;
  name_en: string;
  name_ar: string;
  category_kind: string;
  business_travel_id: string | null;
  description: string | null;
  currency: string;
  advance_percentage: string;
  return_percentage: string;
  pay_with_salary: boolean;
  is_active: boolean;
  created_at: string;
  updated_at: string;
}

interface RangeDTO {
  id: string;
  category_id: string;
  arrival_country: string;
  arrival_city: string;
  is_domestic: boolean;
  from_day: number;
  to_day: number;
  per_diem_amount: string;
  currency: string;
  is_active: boolean;
  created_at: string;
  updated_at: string;
}


async function _request<T>(path: string, init?: RequestInit): Promise<T> {
  const resp = await fetch(path, {
    credentials: "include",
    headers: { "Content-Type": "application/json" },
    ...init,
  });
  if (!resp.ok) {
    let detail = resp.statusText;
    try {
      const body = await resp.json();
      detail = body.detail || body.message || JSON.stringify(body);
    } catch {
      // Non-JSON body
    }
    const err = new Error(detail);
    (err as Error & { status?: number }).status = resp.status;
    throw err;
  }
  if (resp.status === 204) return undefined as T;
  return (await resp.json()) as T;
}


const _CATEGORY_ICONS: Record<string, typeof Crown> = {
  ceo: Crown,
  gms_directors: UserCog,
  managers: Users,
  supervisors: Building2,
  custom: Plane,
};


export function TravelPerDiemEditorFullPage() {
  const { t, i18n } = useTranslation();
  const isAr = i18n.language === "ar";
  const legalEntityId = useLegalEntityId();

  const [categories, setCategories] = useState<CategoryDTO[]>([]);
  const [selectedCategoryId, setSelectedCategoryId] = useState<string | null>(
    null,
  );
  const [ranges, setRanges] = useState<RangeDTO[]>([]);
  const [activeTab, setActiveTab] = useState<"domestic" | "international">(
    "domestic",
  );
  const [loadingCats, setLoadingCats] = useState(true);
  const [loadingRanges, setLoadingRanges] = useState(false);
  const [headerDirty, setHeaderDirty] = useState(false);
  const [headerDraft, setHeaderDraft] = useState<Partial<CategoryDTO>>({});
  const [errorMessage, setErrorMessage] = useState<string | null>(null);

  const selectedCategory = useMemo(
    () => categories.find((c) => c.id === selectedCategoryId) ?? null,
    [categories, selectedCategoryId],
  );

  // Initial load — categories
  useEffect(() => {
    if (!legalEntityId) return;
    setLoadingCats(true);
    _request<CategoryDTO[]>(
      `/api/travel/per-diem/categories?legal_entity_id=${legalEntityId}`,
    )
      .then((cs) => {
        setCategories(cs);
        if (cs.length > 0 && selectedCategoryId === null) {
          setSelectedCategoryId(cs[0].id);
        }
      })
      .catch((e) => setErrorMessage(String(e?.message ?? e)))
      .finally(() => setLoadingCats(false));
  }, [legalEntityId, selectedCategoryId]);

  // Load ranges whenever the selected category changes
  useEffect(() => {
    if (!selectedCategoryId) {
      setRanges([]);
      return;
    }
    setLoadingRanges(true);
    _request<RangeDTO[]>(
      `/api/travel/per-diem/ranges?category_id=${selectedCategoryId}`,
    )
      .then(setRanges)
      .catch((e) => setErrorMessage(String(e?.message ?? e)))
      .finally(() => setLoadingRanges(false));
  }, [selectedCategoryId]);

  // Reset header draft whenever the selected category changes
  useEffect(() => {
    if (selectedCategory) {
      setHeaderDraft({
        business_travel_id: selectedCategory.business_travel_id ?? "",
        description: selectedCategory.description ?? "",
        currency: selectedCategory.currency,
        advance_percentage: selectedCategory.advance_percentage,
        return_percentage: selectedCategory.return_percentage,
        pay_with_salary: selectedCategory.pay_with_salary,
      });
      setHeaderDirty(false);
    }
  }, [selectedCategory]);

  // ---- PR C UX closure: drag-drop reorder of range rows.
  // Sort order persists in localStorage per (LE, category, tab) so HR
  // can see their preferred tier order each time they open the
  // editor. The server is untouched — `from_day` ASC remains the
  // logical tier order; this is purely an HR ergonomics affordance.
  const _sortKey = useMemo(() => {
    if (!selectedCategoryId) return "";
    return `solvait-aihr.tdpe.order.${legalEntityId ?? "_"}.${selectedCategoryId}.${activeTab}`;
  }, [legalEntityId, selectedCategoryId, activeTab]);

  const [rangeOrder, setRangeOrder] = useState<string[]>(() => {
    if (typeof window === "undefined" || !_sortKey) return [];
    try {
      const raw = window.localStorage.getItem(_sortKey);
      return raw ? (JSON.parse(raw) as string[]) : [];
    } catch {
      return [];
    }
  });
  useEffect(() => {
    if (typeof window === "undefined" || !_sortKey) return;
    try {
      const raw = window.localStorage.getItem(_sortKey);
      setRangeOrder(raw ? (JSON.parse(raw) as string[]) : []);
    } catch {
      setRangeOrder([]);
    }
  }, [_sortKey]);

  const filteredRanges = useMemo(() => {
    const base = ranges.filter(
      (r) => r.is_domestic === (activeTab === "domestic"),
    );
    const natural = [...base].sort((a, b) => a.from_day - b.from_day);
    if (rangeOrder.length === 0) return natural;
    const seen = new Set<string>();
    const head: RangeDTO[] = [];
    for (const id of rangeOrder) {
      const r = natural.find((x) => x.id === id);
      if (r) {
        head.push(r);
        seen.add(id);
      }
    }
    const tail = natural.filter((r) => !seen.has(r.id));
    return [...head, ...tail];
  }, [ranges, activeTab, rangeOrder]);

  const [draggedRangeId, setDraggedRangeId] = useState<string | null>(null);

  function _persistOrder(next: string[]) {
    setRangeOrder(next);
    if (typeof window === "undefined" || !_sortKey) return;
    try {
      window.localStorage.setItem(_sortKey, JSON.stringify(next));
    } catch {
      // localStorage unavailable — non-fatal.
    }
  }

  function _onDragStart(id: string) {
    setDraggedRangeId(id);
  }
  function _onDragOver(e: React.DragEvent<HTMLTableRowElement>) {
    e.preventDefault();
  }
  function _onDropOnRow(targetId: string) {
    if (!draggedRangeId || draggedRangeId === targetId) {
      setDraggedRangeId(null);
      return;
    }
    const currentIds = filteredRanges.map((r) => r.id);
    const fromIdx = currentIds.indexOf(draggedRangeId);
    const toIdx = currentIds.indexOf(targetId);
    if (fromIdx === -1 || toIdx === -1) {
      setDraggedRangeId(null);
      return;
    }
    const next = [...currentIds];
    next.splice(fromIdx, 1);
    next.splice(toIdx, 0, draggedRangeId);
    _persistOrder(next);
    setDraggedRangeId(null);
  }

  async function _saveHeader() {
    if (!selectedCategory) return;
    setErrorMessage(null);
    try {
      const updated = await _request<CategoryDTO>(
        `/api/travel/per-diem/categories/${selectedCategory.id}`,
        { method: "PATCH", body: JSON.stringify(headerDraft) },
      );
      setCategories((cs) =>
        cs.map((c) => (c.id === updated.id ? updated : c)),
      );
      setHeaderDirty(false);
    } catch (e: unknown) {
      setErrorMessage(e instanceof Error ? e.message : String(e));
    }
  }

  function _patchHeader(field: keyof CategoryDTO, value: unknown) {
    setHeaderDraft((h) => ({ ...h, [field]: value }));
    setHeaderDirty(true);
  }

  // Optimistic range editing — local state holds the in-flight edit;
  // commit hits PATCH /ranges/{id}; on 400 we surface inline.
  const [rangeDrafts, setRangeDrafts] = useState<Record<string, Partial<RangeDTO>>>({});
  const [rangeErrors, setRangeErrors] = useState<Record<string, string>>({});

  function _patchRangeDraft(id: string, patch: Partial<RangeDTO>) {
    setRangeDrafts((d) => ({ ...d, [id]: { ...(d[id] ?? {}), ...patch } }));
  }

  async function _saveRange(rng: RangeDTO) {
    const draft = rangeDrafts[rng.id];
    if (!draft || Object.keys(draft).length === 0) return;
    setRangeErrors((e) => ({ ...e, [rng.id]: "" }));
    try {
      const updated = await _request<RangeDTO>(
        `/api/travel/per-diem/ranges/${rng.id}`,
        { method: "PATCH", body: JSON.stringify(draft) },
      );
      setRanges((rs) => rs.map((r) => (r.id === updated.id ? updated : r)));
      setRangeDrafts((d) => {
        const next = { ...d };
        delete next[rng.id];
        return next;
      });
    } catch (e: unknown) {
      const msg = e instanceof Error ? e.message : String(e);
      // Mutaba'aa veto messages surface here (e.g., "per_diem_amount = 0
      // would breach the customer-set per diem floor")
      setRangeErrors((errs) => ({ ...errs, [rng.id]: msg }));
    }
  }

  async function _deleteRange(id: string) {
    try {
      await _request(`/api/travel/per-diem/ranges/${id}`, {
        method: "DELETE",
      });
      setRanges((rs) => rs.filter((r) => r.id !== id));
    } catch (e: unknown) {
      setErrorMessage(e instanceof Error ? e.message : String(e));
    }
  }

  async function _addRange() {
    if (!selectedCategory) return;
    try {
      const created = await _request<RangeDTO>(
        `/api/travel/per-diem/ranges`,
        {
          method: "POST",
          body: JSON.stringify({
            category_id: selectedCategory.id,
            arrival_country:
              activeTab === "domestic" ? "Saudi Arabia" : "United Arab Emirates",
            arrival_city: activeTab === "domestic" ? "Riyadh" : "Dubai",
            is_domestic: activeTab === "domestic",
            from_day: 1,
            to_day: 99,
            per_diem_amount:
              activeTab === "domestic" ? "800.00" : "1500.00",
          }),
        },
      );
      setRanges((rs) => [...rs, created]);
    } catch (e: unknown) {
      setErrorMessage(e instanceof Error ? e.message : String(e));
    }
  }

  return (
    <div className="space-y-4">
      <PageHeader
        title={t("travel.editor.title", "Per Diem Catalog")}
        subtitle={t(
          "travel.editor.subtitle",
          "Per-LE per-category per-destination per-day-range matrix. Edit amounts, % splits, and tiered windows. Mutaba'aa enforces floors inline.",
        )}
      />

      {errorMessage && (
        <div className="flex items-start gap-2 rounded-md border border-rose-200 bg-rose-50 p-3 text-sm text-rose-800">
          <CircleAlert className="mt-0.5 h-4 w-4 flex-shrink-0" />
          <div className="flex-1">{errorMessage}</div>
          <button
            type="button"
            onClick={() => setErrorMessage(null)}
            className="text-rose-600 hover:text-rose-800"
          >
            <X className="h-4 w-4" />
          </button>
        </div>
      )}

      <div className="grid grid-cols-1 gap-4 lg:grid-cols-[240px_1fr]">
        {/* ============ Left sidebar — categories list ============ */}
        <aside className="rounded-lg border border-border-default bg-surface-default p-2">
          <h2 className="px-2 py-1 text-xs font-semibold uppercase tracking-wide text-text-tertiary">
            {t("travel.editor.categories", "Categories")}
          </h2>
          {loadingCats && (
            <div className="px-2 py-1 text-sm text-text-tertiary">
              {t("common.loading", "Loading…")}
            </div>
          )}
          <ul className="space-y-1">
            {categories.map((c) => {
              const Icon = _CATEGORY_ICONS[c.category_kind] ?? Plane;
              const selected = c.id === selectedCategoryId;
              return (
                <li key={c.id}>
                  <button
                    type="button"
                    onClick={() => setSelectedCategoryId(c.id)}
                    className={`flex w-full items-center gap-2 rounded-md px-2 py-1.5 text-start text-sm ${
                      selected
                        ? "bg-brand-deep text-white"
                        : "text-text-secondary hover:bg-surface-subtle"
                    }`}
                  >
                    <Icon className="h-4 w-4 flex-shrink-0" />
                    <span className="truncate">
                      {isAr ? c.name_ar : c.name_en}
                    </span>
                  </button>
                </li>
              );
            })}
          </ul>
        </aside>

        {/* ============ Right pane — header + tabs + ranges ============ */}
        <section className="space-y-4">
          {selectedCategory && (
            <>
              {/* ---- Category header card */}
              <div className="rounded-lg border border-border-default bg-surface-default p-4">
                <div className="grid grid-cols-1 gap-3 sm:grid-cols-2 lg:grid-cols-3">
                  <HeaderField
                    label={t(
                      "travel.editor.business_travel_id",
                      "Business Travel ID",
                    )}
                    value={(headerDraft.business_travel_id ?? "") as string}
                    onChange={(v) => _patchHeader("business_travel_id", v)}
                  />
                  <HeaderField
                    label={t("travel.editor.currency", "Currency")}
                    value={(headerDraft.currency ?? "SAR") as string}
                    onChange={(v) => _patchHeader("currency", v)}
                  />
                  <HeaderField
                    label={t(
                      "travel.editor.advance_pct",
                      "Advance %",
                    )}
                    type="number"
                    value={String(headerDraft.advance_percentage ?? "")}
                    onChange={(v) => _patchHeader("advance_percentage", v)}
                  />
                  <HeaderField
                    label={t(
                      "travel.editor.return_pct",
                      "Return % (after trip)",
                    )}
                    type="number"
                    value={String(headerDraft.return_percentage ?? "")}
                    onChange={(v) => _patchHeader("return_percentage", v)}
                  />
                  <HeaderField
                    label={t("travel.editor.description", "Description")}
                    value={(headerDraft.description ?? "") as string}
                    onChange={(v) => _patchHeader("description", v)}
                    widthClass="col-span-1 sm:col-span-2"
                  />
                  <label className="col-span-1 flex items-end gap-2 text-sm">
                    <input
                      type="checkbox"
                      checked={!!headerDraft.pay_with_salary}
                      onChange={(e) =>
                        _patchHeader("pay_with_salary", e.target.checked)
                      }
                    />
                    {t(
                      "travel.editor.pay_with_salary",
                      "Pay with monthly salary (instead of OFF-CYCLE)",
                    )}
                  </label>
                </div>
                {headerDirty && (
                  <div className="mt-3 flex justify-end gap-2">
                    <button
                      type="button"
                      onClick={() => {
                        setHeaderDraft({
                          business_travel_id:
                            selectedCategory.business_travel_id ?? "",
                          description: selectedCategory.description ?? "",
                          currency: selectedCategory.currency,
                          advance_percentage:
                            selectedCategory.advance_percentage,
                          return_percentage:
                            selectedCategory.return_percentage,
                          pay_with_salary: selectedCategory.pay_with_salary,
                        });
                        setHeaderDirty(false);
                      }}
                      className="rounded-md border border-border-default bg-surface-default px-3 py-1.5 text-sm text-text-secondary hover:bg-surface-subtle"
                    >
                      {t("common.cancel", "Cancel")}
                    </button>
                    <button
                      type="button"
                      onClick={_saveHeader}
                      className="inline-flex items-center gap-1 rounded-md bg-brand-deep px-3 py-1.5 text-sm font-medium text-white hover:bg-brand-blue"
                    >
                      <Save className="h-3.5 w-3.5" />
                      {t("common.save", "Save")}
                    </button>
                  </div>
                )}
              </div>

              {/* ---- Domestic / International tabs */}
              <div className="flex gap-2 border-b border-border-default">
                <TabButton
                  active={activeTab === "domestic"}
                  onClick={() => setActiveTab("domestic")}
                  label={t("travel.editor.tab_domestic", "Domestic")}
                />
                <TabButton
                  active={activeTab === "international"}
                  onClick={() => setActiveTab("international")}
                  label={t("travel.editor.tab_international", "International")}
                />
              </div>

              {/* ---- Ranges table */}
              <div className="rounded-lg border border-border-default bg-surface-default">
                <table className="w-full text-sm">
                  <thead className="bg-surface-subtle text-xs uppercase tracking-wide text-text-tertiary">
                    <tr>
                      <th
                        className="w-8 px-2 py-2"
                        aria-label={t(
                          "travel.editor.drag_handle_aria",
                          "Drag to reorder",
                        )}
                      />
                      <th className="px-3 py-2 text-start">
                        {t("travel.editor.country", "Country")}
                      </th>
                      <th className="px-3 py-2 text-start">
                        {t("travel.editor.city", "City")}
                      </th>
                      <th className="px-3 py-2 text-end">
                        {t("travel.editor.from_day", "From day")}
                      </th>
                      <th className="px-3 py-2 text-end">
                        {t("travel.editor.to_day", "To day")}
                      </th>
                      <th className="px-3 py-2 text-end">
                        {t("travel.editor.amount", "Per diem amount")}
                      </th>
                      <th className="px-3 py-2 text-center">
                        {t("travel.editor.actions", "")}
                      </th>
                    </tr>
                  </thead>
                  <tbody>
                    {loadingRanges && (
                      <tr>
                        <td
                          colSpan={7}
                          className="px-3 py-4 text-center text-text-tertiary"
                        >
                          {t("common.loading", "Loading…")}
                        </td>
                      </tr>
                    )}
                    {!loadingRanges && filteredRanges.length === 0 && (
                      <tr>
                        <td
                          colSpan={7}
                          className="px-3 py-6 text-center text-text-tertiary"
                        >
                          {t(
                            "travel.editor.empty_ranges",
                            "No ranges yet for this tab. Click + Add range below.",
                          )}
                        </td>
                      </tr>
                    )}
                    {filteredRanges.map((rng) => {
                      const draft = rangeDrafts[rng.id] ?? {};
                      const merged = { ...rng, ...draft };
                      const dirty = Object.keys(draft).length > 0;
                      const inlineError = rangeErrors[rng.id];
                      const beingDragged = draggedRangeId === rng.id;
                      return (
                        <tr
                          key={rng.id}
                          draggable
                          onDragStart={() => _onDragStart(rng.id)}
                          onDragOver={_onDragOver}
                          onDrop={() => _onDropOnRow(rng.id)}
                          className={`border-t border-border-default ${
                            beingDragged ? "opacity-50" : ""
                          }`}
                          data-testid={`range-row-${rng.id}`}
                        >
                          <td
                            className="cursor-grab px-2 py-2 text-text-tertiary"
                            aria-label={t(
                              "travel.editor.drag_handle_aria",
                              "Drag to reorder",
                            )}
                          >
                            <GripVertical className="h-4 w-4" />
                          </td>
                          <td className="px-3 py-2">
                            <InlineInput
                              value={merged.arrival_country}
                              onChange={(v) =>
                                _patchRangeDraft(rng.id, {
                                  arrival_country: v,
                                })
                              }
                            />
                          </td>
                          <td className="px-3 py-2">
                            <InlineInput
                              value={merged.arrival_city}
                              onChange={(v) =>
                                _patchRangeDraft(rng.id, {
                                  arrival_city: v,
                                })
                              }
                            />
                          </td>
                          <td className="px-3 py-2 text-end">
                            <InlineInput
                              type="number"
                              value={String(merged.from_day)}
                              onChange={(v) =>
                                _patchRangeDraft(rng.id, {
                                  from_day: parseInt(v) || 1,
                                })
                              }
                              align="end"
                            />
                          </td>
                          <td className="px-3 py-2 text-end">
                            <InlineInput
                              type="number"
                              value={String(merged.to_day)}
                              onChange={(v) =>
                                _patchRangeDraft(rng.id, {
                                  to_day: parseInt(v) || 99,
                                })
                              }
                              align="end"
                            />
                          </td>
                          <td className="px-3 py-2 text-end">
                            <InlineInput
                              type="number"
                              value={String(merged.per_diem_amount)}
                              onChange={(v) =>
                                _patchRangeDraft(rng.id, {
                                  per_diem_amount: v,
                                })
                              }
                              align="end"
                            />
                            {inlineError && (
                              <p className="mt-1 text-xs text-rose-700">
                                {inlineError}
                              </p>
                            )}
                          </td>
                          <td className="px-3 py-2 text-center">
                            {dirty && (
                              <button
                                type="button"
                                onClick={() => _saveRange(rng)}
                                className="mr-1 inline-flex items-center rounded-md bg-brand-deep px-2 py-1 text-xs text-white hover:bg-brand-blue"
                              >
                                <Save className="h-3 w-3" />
                              </button>
                            )}
                            <button
                              type="button"
                              onClick={() => _deleteRange(rng.id)}
                              className="text-rose-600 hover:text-rose-800"
                              aria-label={t("common.remove", "Remove")}
                            >
                              <Trash2 className="h-3.5 w-3.5" />
                            </button>
                          </td>
                        </tr>
                      );
                    })}
                  </tbody>
                </table>
                <div className="flex items-center justify-between gap-3 border-t border-border-default p-3">
                  <button
                    type="button"
                    onClick={_addRange}
                    className="inline-flex items-center gap-1 rounded-md border border-border-default bg-surface-default px-3 py-1.5 text-sm text-text-secondary hover:bg-surface-subtle"
                  >
                    <PlusCircle className="h-3.5 w-3.5" />
                    {t("travel.editor.add_range", "+ Add range")}
                  </button>
                  {filteredRanges.length > 1 && (
                    <p className="flex items-center gap-2 text-xs italic text-text-tertiary">
                      <GripVertical className="h-3 w-3" />
                      {t(
                        "travel.editor.drag_hint",
                        "Drag rows to reorder — your sort order persists locally.",
                      )}
                      {rangeOrder.length > 0 && (
                        <button
                          type="button"
                          onClick={() => _persistOrder([])}
                          className="not-italic text-brand-deep underline hover:text-brand-blue"
                        >
                          {t("common.reset", "reset")}
                        </button>
                      )}
                    </p>
                  )}
                </div>
              </div>
            </>
          )}

          {!selectedCategory && !loadingCats && (
            <div className="rounded-lg border border-border-default bg-surface-default p-6 text-center text-sm text-text-tertiary">
              {t(
                "travel.editor.empty_categories",
                "No categories yet. The M7 onboarding hook seeds 4 default categories per LE — verify the LE is provisioned.",
              )}
            </div>
          )}
        </section>
      </div>
    </div>
  );
}


function HeaderField({
  label,
  value,
  onChange,
  type = "text",
  widthClass,
}: {
  label: string;
  value: string;
  onChange: (v: string) => void;
  type?: string;
  widthClass?: string;
}) {
  return (
    <div className={widthClass ?? ""}>
      <label className="block text-xs font-medium text-text-tertiary">
        {label}
      </label>
      <input
        type={type}
        value={value}
        onChange={(e) => onChange(e.target.value)}
        className="mt-1 w-full rounded-md border border-border-default bg-surface-default px-2 py-1.5 text-sm"
      />
    </div>
  );
}


function InlineInput({
  value,
  onChange,
  type = "text",
  align = "start",
}: {
  value: string;
  onChange: (v: string) => void;
  type?: string;
  align?: "start" | "end";
}) {
  return (
    <input
      type={type}
      value={value}
      onChange={(e) => onChange(e.target.value)}
      className={`w-full rounded-md border border-transparent bg-transparent px-2 py-1 text-sm hover:border-border-default focus:border-brand-deep focus:bg-surface-default focus:outline-none ${
        align === "end" ? "text-end" : ""
      }`}
    />
  );
}


function TabButton({
  active,
  onClick,
  label,
}: {
  active: boolean;
  onClick: () => void;
  label: string;
}) {
  return (
    <button
      type="button"
      onClick={onClick}
      className={`-mb-px border-b-2 px-3 py-2 text-sm font-medium ${
        active
          ? "border-brand-deep text-brand-deep"
          : "border-transparent text-text-secondary hover:text-text-primary"
      }`}
    >
      {label}
    </button>
  );
}


export default TravelPerDiemEditorFullPage;
